import { gql } from '@apollo/client'

export const getAllUsers = gql`
    query GetAllUsers($pageSize: Int, $page: Int, $rcUserId: String) {
        getAllUsers(pageSize: $pageSize, page: $page, rcUserId: $rcUserId) {
            users {
                id
                createdAt
                updatedAt
                email
                role
                appleSub
                googleSub
                isAgreementChecked
                premiumExpiryDate
                fcmId
                rcUserId
                store
                isPremium
                totalCredit {
                    total
                    dailyCredit
                }
            }
            totalCount
        }
    }
`

export const getUser = gql`
    query GetUser($userId: String!) {
        getUser(userId: $userId) {
            id
            createdAt
            updatedAt
            email
            role
            appleSub
            googleSub
            isAgreementChecked
            premiumExpiryDate
            fcmId
            rcUserId
            store
            firstSeenLocation
            lastSeenLocation
            isPremium
            totalCredit {
                total
                dailyCredit
            }
            usedCreditCount
        }
    }
`

// ==================== AUDIO QUERIES ====================

export const getAllAudios = gql`
    query GetAllAudios($status: StatusEnum, $generationSource: GenerationSourceEnum, $rcUserId: String, $pageSize: Int, $page: Int, $startDate: DateTime, $endDate: DateTime) {
        getAllAudios(status: $status, generationSource: $generationSource, rcUserId: $rcUserId, pageSize: $pageSize, page: $page, startDate: $startDate, endDate: $endDate) {
            audios {
                id
                createdAt
                updatedAt
                url
                status
                prompt
                title
                progressRatio
                duration
                generationSource
                imageUrl
                tags
                user {
                    id
                    rcUserId
                    email
                }
            }
            totalCount
        }
    }
`

export const getAudioAdmin = gql`
    query GetAudioAdmin($audioId: String!) {
        getAudioAdmin(audioId: $audioId) {
            id
            createdAt
            updatedAt
            taskId
            userId
            url
            status
            prompt
            lyricsPrompt
            title
            progressRatio
            duration
            errorReason
            sampleRate
            bitrate
            format
            imageUrl
            streamAudioUrl
            tags
            generationSource
            user {
                id
                rcUserId
                email
            }
        }
    }
`

export const getDailyAudioStats = gql`
    query GetDailyAudioStats($endDate: String, $startDate: String) {
        getDailyAudioStats(endDate: $endDate, startDate: $startDate) {
            date
            count
        }
    }
`

export const getAudioStatusStats = gql`
    query GetAudioStatusStats($endDate: String, $startDate: String) {
        getAudioStatusStats(endDate: $endDate, startDate: $startDate) {
            failed
            running
            in_queue
            throttled
            succeeded
            pending
        }
    }
`

export const getAudioSourceStats = gql`
    query GetAudioSourceStats($endDate: String, $startDate: String) {
        getAudioSourceStats(endDate: $endDate, startDate: $startDate) {
            fal_ai
            kie_ai
        }
    }
`

export const dashboardUserAudios = gql`
    query DashboardUserAudios($rcUserId: String!, $pageSize: Int, $page: Int) {
        getAllAudios(rcUserId: $rcUserId, pageSize: $pageSize, page: $page) {
            audios {
                id
                title
                prompt
                imageUrl
                status
                generationSource
                duration
                createdAt
                updatedAt
                url
                tags
            }
            totalCount
        }
    }
`

// ==================== MUSIC STYLE QUERIES ====================

export const getMusicStyles = gql`
    query GetMusicStyles {
        getMusicStyles {
            id
            name
            icon
            createdAt
            updatedAt
        }
    }
`

export const getMusicStyle = gql`
    query GetMusicStyle($id: String!) {
        getMusicStyle(id: $id) {
            id
            name
            icon
            createdAt
            updatedAt
        }
    }
`

// ==================== GENRE QUERIES ====================

export const getGenres = gql`
    query GetGenres {
        getGenres {
            id
            name
            icon
            prompt
            createdAt
            updatedAt
        }
    }
`

export const getGenre = gql`
    query GetGenre($id: String!) {
        getGenre(id: $id) {
            id
            name
            icon
            prompt
            createdAt
            updatedAt
        }
    }
`

// ==================== CREDIT QUERIES ====================

export const getAllCreditsAdmin = gql`
    query getAllCreditsAdmin {
        getAllCredits {
            amount
            rcEventId
            id
            user {
                id
                email
            }
            date
        }
    }
`

export const getUserCreditHistory = gql`
    query GetUserCreditHistory($userId: String!) {
        getUserCreditHistory(userId: $userId) {
            id
            userId
            creditId
            amount
            transactionId
            createdAt
            updatedAt
        }
    }
`

export const getCredit = gql`
    query GetCredit($creditId: String!) {
        getCredit(creditId: $creditId) {
            id
            createdAt
            updatedAt
            date
            amount
            rcEventId
            productName
            productType
            expiryDate
            transactionId
            isPromo
        }
    }
`

export const getCreditStats = gql`
    query GetCreditStats($endDate: String, $startDate: String) {
        getCreditStats(endDate: $endDate, startDate: $startDate) {
            date
            creditCounts {
                weekly
                monthly
                yearly
                tier1
                tier2
                tier3
                oneTime
            }
            totalCount
        }
    }
`

// Removed getCreditConfig - use getSystem instead

// ==================== DASHBOARD & STATS QUERIES ====================

export const getBasicStats = gql`
    query GetBasicStats {
        getBasicStats {
            totalUsers
            totalAudios
            activeSubscriptions
            lastAudioDate
        }
    }
`

// ==================== SYSTEM QUERIES ====================

export const getSystem = gql`
    query GetSystem($systemType: SystemTypeEnum!) {
        getSystem(systemType: $systemType) {
            id
            type
            creditCost
            createdAt
            updatedAt
        }
    }
`

// ==================== SUPPORT QUERIES ====================

export const supportAdmin = gql`
    query SupportAdmin($rcUserId: String!) {
        supportAdmin(rcUserId: $rcUserId) {
            credits {
                id
                createdAt
                updatedAt
                date
                amount
                rcEventId
                productName
                productType
                expiryDate
                transactionId
                isPromo
            }
            user {
                id
                createdAt
                updatedAt
                email
                role
                appleSub
                googleSub
                userName
                isAgreementChecked
                premiumExpiryDate
                fcmId
                rcUserId
                store
                firstSeenLocation
                totalCredit {
                    total
                }
                usedCreditCount
                isPremium
                lastSeenLocation
            }
            audioAnalyticData {
                succeeded
                failed
                total
                refund
            }
        }
    }
`

// ==================== CREDIT PRODUCT QUERIES ====================

export const getAllCreditProducts = gql`
    query GetAllCreditProducts($filter: CreditProductFilterInput, $pagination: CreditProductPaginationInput!) {
        getAllCreditProducts(filter: $filter, pagination: $pagination) {
            total
            hasMore
            items {
                id
                name
                amount
                startDate
                endDate
                limit
                price
                type
                createdAt
                updatedAt
                creditCount
                isActive
                isExpired
                isUpcoming
            }
        }
    }
`

export const getCreditProductById = gql`
    query GetCreditProductById($getCreditProductByIdId: String!) {
        getCreditProductById(id: $getCreditProductByIdId) {
            id
            name
            amount
            startDate
            endDate
            limit
            price
            type
            createdAt
            updatedAt
            creditCount
            isActive
            isExpired
            isUpcoming
        }
    }
`

// ==================== TOOL QUERIES ====================

export const getAllTools = gql`
    query GetAllTools($filter: StudioFilterInput, $page: Int, $pageSize: Int, $sortByVideoCount: SortOrder) {
        getAllTools(filter: $filter, page: $page, pageSize: $pageSize, sortByVideoCount: $sortByVideoCount) {
            tools {
                id
                name
                imageType
                bannerUrl
                videoUrl
                webmUrl
                webpUrl
                rowNumber
                templateId
                description
                prompt
                isFeatured
                duration
                model
                categoryId
                cost
                provider
                createdAt
                updatedAt
                videoCount
                aspectRatios {
                    id
                    width
                    height
                    name
                    aspectRatioX
                    aspectRatioY
                    createdAt
                    updatedAt
                    toolId
                }
            }
            count
        }
    }
`

export const getTool = gql`
    query GetTool($getToolId: String!) {
        getTool(id: $getToolId) {
            id
            name
            imageType
            bannerUrl
            videoUrl
            rowNumber
            templateId
            description
            prompt
            isFeatured
            duration
            categoryId
            cost
            provider
            createdAt
            updatedAt
            videoCount
            badgeText
            badgeIcon
            badgeTextColor
            badgeBackgroundColor
            category {
                id
                name
            }
            webmUrl
            webpUrl
            aspectRatios {
                id
                width
                height
                name
                aspectRatioX
                aspectRatioY
                createdAt
                updatedAt
                toolId
            }
        }
    }
`

// ==================== NOTIFICATION QUERIES ====================

export const getAdminNotifications = gql`
    query GetAdminNotifications($userId: String, $page: Int, $pageSize: Int) {
        getAdminNotifications(userId: $userId, page: $page, pageSize: $pageSize) {
            notifications {
                id
                userId
                type
                title
                body
                data
                isRead
                sentAt
                readAt
                createdAt
                updatedAt
                user {
                    id
                    rcUserId
                    email
                }
            }
            totalCount
        }
    }
`

// ==================== EXPLORE QUERIES ====================

export const getAllExploreCategories = gql`
    query GetAllExploreCategories {
        getAllExploreCategories {
            id
            name
            order
            isActive
            audios {
                id
            }
        }
    }
`

export const getAllExploreAudios = gql`
    query GetAllExploreAudios {
        getAllExploreAudios {
            id
            name
            imageUrl
            audioUrl
            prompt
            lyrics
            order
            isActive
            categoryId
        }
    }
`

export const getExploreCategory = gql`
    query GetExploreCategory($id: String!) {
        getExploreCategory(id: $id) {
            id
            name
            order
            isActive
            audios {
                id
                name
                imageUrl
                audioUrl
                prompt
                order
                isActive
            }
        }
    }
`

export const getExploreAudio = gql`
    query GetExploreAudio($id: String!) {
        getExploreAudio(id: $id) {
            id
            name
            imageUrl
            audioUrl
            prompt
            lyrics
            order
            isActive
            categoryId
        }
    }
`

// ==================== WEBHOOK LOG QUERIES ====================

export const getWebhookLogs = gql`
    query GetWebhookLogs($source: WebhookSourceEnum, $taskId: String, $audioId: String, $endpoint: String, $statusCode: Int, $page: Int, $pageSize: Int) {
        getWebhookLogs(source: $source, taskId: $taskId, audioId: $audioId, endpoint: $endpoint, statusCode: $statusCode, page: $page, pageSize: $pageSize) {
            logs {
                id
                createdAt
                source
                endpoint
                method
                headers
                body
                query
                statusCode
                response
                errorMessage
                processingMs
                ip
                userAgent
                taskId
                audioId
            }
            totalCount
        }
    }
`

export const getWebhookLog = gql`
    query GetWebhookLog($id: String!) {
        getWebhookLog(id: $id) {
            id
            createdAt
            source
            endpoint
            method
            headers
            body
            query
            statusCode
            response
            errorMessage
            processingMs
            ip
            userAgent
            taskId
            audioId
            vocalId
        }
    }
`

// ============== VOCAL QUERIES ==============

export const GET_ALL_VOCALS = gql`
    query GetAllVocals(
        $userId: String
        $search: String
        $page: Int
        $pageSize: Int
    ) {
        getAllVocals(
            userId: $userId
            search: $search
            page: $page
            pageSize: $pageSize
        ) {
            id
            createdAt
            updatedAt
            userId
            vocalUrl
            vocalGender
            title
            duration
            isVisible
        }
    }
`

export const GET_USER_VOCALS = gql`
    query GetUserVocals($skip: Int, $take: Int) {
        getUserVocals(skip: $skip, take: $take) {
            id
            createdAt
            updatedAt
            userId
            vocalUrl
            vocalGender
            title
            duration
            isVisible
        }
    }
`

export const GET_VOCAL = gql`
    query GetVocal($id: ID!) {
        getVocal(id: $id) {
            id
            createdAt
            updatedAt
            userId
            user {
                id
                email
                rcUserId
                isPremium
            }
            vocalUrl
            vocalGender
            title
            duration
            isVisible
            audios {
                id
                createdAt
                url
                title
                status
                duration
                tags
                imageUrl
            }
        }
    }
`


